import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { FilterType } from '@/types/asset-search.types';

import { isValidAssetSearchFilter } from '@/components/filters/search/search.utils';

import { getAppConfig } from '@/utils/window.utils';

/**
 * Custom hook to handle filters via search bar
 * @returns all methods to handle filters
 */
export const useSearch = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const daysFilter = searchParams.get(FilterType.NoOfDays);
    const rawAssetIdFilter = searchParams.get(FilterType.AssetId);
    const assetIdFilter = isValidAssetSearchFilter(rawAssetIdFilter) ? rawAssetIdFilter : null;
    const typeFilter = searchParams.get(FilterType.Type)?.split('_') ?? [];
    const hasFilter = typeFilter.length > 0 || Boolean(assetIdFilter);

    const addFilter = useCallback(
        (filter: FilterType, value: string) => {
            if (filter === FilterType.AssetId && !isValidAssetSearchFilter(value)) {
                return;
            }

            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set(encodeURI(filter), encodeURI(value));
            setSearchParams(newSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const removeFilter = useCallback(
        (filter: FilterType) => {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete(filter);
            setSearchParams(newSearchParams);
        },
        [searchParams, setSearchParams]
    );

    const removeAllFilters = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete(FilterType.AssetId);
        newSearchParams.delete(FilterType.Type);
        setSearchParams(newSearchParams);
    };

    useEffect(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        let hasUpdatedSearchParams = false;

        if (rawAssetIdFilter && !isValidAssetSearchFilter(rawAssetIdFilter)) {
            newSearchParams.delete(FilterType.AssetId);
            hasUpdatedSearchParams = true;
        }

        if (!daysFilter) {
            newSearchParams.set(FilterType.NoOfDays, getAppConfig().filters.noOfDays.default);
            hasUpdatedSearchParams = true;
        }

        if (hasUpdatedSearchParams) {
            setSearchParams(newSearchParams);
        }
    }, [daysFilter, rawAssetIdFilter, searchParams, setSearchParams]);

    return { addFilter, removeFilter, removeAllFilters, daysFilter, assetIdFilter, typeFilter, hasFilter };
};
